import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Notice, Spinner } from '../components/ui';
import { ApiError, downloadFile } from '../lib/api';
import { relativeDate } from '../lib/school';
import { useNudge } from '../lib/people';
import { DirectionTag, SCORE_MODE_OPTIONS } from '../components/ScoreMode';
import { dayMonth, useTargets } from '../lib/development';
import { hubParams, useHub, useWellbeing, type Hub, type HubArea, type HubPeriod, type HubQuery } from '../lib/hub';
import './Page.css';
import './Hub.css';

/** A period label mid-sentence: "the 4 weeks before", "school year 2025/26", but term names keep their capital ("Summer 2026 term"). */
const since = (label: string) => label.replace(/^(The|School) /, (w) => w.toLowerCase());

const PERIODS: { key: HubPeriod; label: string }[] = [
  { key: 'term', label: 'Term' },
  { key: '4w', label: 'Last 4 weeks' },
  { key: 'year', label: 'School year' },
  { key: 'custom', label: 'Choose dates' },
];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Custom dates the API accepts: both set, in order, two years or less. */
const rangeProblem = (from: string, to: string) => {
  if (!from || !to) return 'Choose a start and an end date.';
  if (to < from) return 'The end date has to be after the start date.';
  if ((Date.parse(to) - Date.parse(from)) / 86_400_000 > 730) return 'Choose two years or less.';
  return null;
};

const one = (n: number | null | undefined) => (n == null ? '–' : n.toFixed(1));

/**
 * A team figure the server held back (services/leadershipHub.ts): fewer than
 * minGroup teachers behind it, not counting the leader looking, or a filter
 * too narrow to show scores without singling someone out.
 */
const heldBack = (x: { teachers: number; average: number | null }, h: Hub) =>
  !!h.filterWithheld || x.teachers < h.minGroup || (h.school.scoreMode !== 'trends' && x.average == null);
const heldBackText = (x: { teachers: number }, h: Hub) =>
  h.filterWithheld
    ? 'Hidden for this filter.'
    : x.teachers === 0
      ? 'No lessons yet.'
      : `Shown once ${h.minGroup} or more teachers have lessons here, not counting you.`;
const day = (ymd: string) => new Date(`${ymd.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
/** "this term", "in the Summer 2026 term", ... for the lessons tile. */
const lessonsWhen = (h: Hub) =>
  h.period.key === '4w'
    ? 'in the last 4 weeks'
    : h.period.key === 'year'
      ? 'this school year'
      : h.period.key === 'custom'
        ? 'in these dates'
        : h.period.terms?.length && h.period.from !== h.period.terms[0].from
          ? `in the ${h.period.label}`
          : 'this term';
const pct = (n: number | null | undefined) => (n == null ? '–' : `${Math.round(n * 100)}%`);

/** "+0.2" / "−0.1" / "no change", from a current and previous average. */
function Change({ now, before }: { now: number | null; before: number | null }) {
  if (now == null || before == null) return null;
  const d = Math.round((now - before) * 10) / 10;
  if (d === 0) return <span className="hub-change">no change</span>;
  return (
    <span className={`hub-change ${d > 0 ? 'is-up' : 'is-down'}`}>
      {d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(1)}
    </span>
  );
}

/**
 * The Leadership Hub: how the team's teaching is developing. Team figures come
 * from every lesson, anonymously (at least five teachers behind each, not
 * counting the viewer); anything about one named teacher only from what they
 * shared. No rankings. See backend services/leadershipHub.ts.
 */
export function HubPage() {
  const [period, setPeriod] = useState<HubPeriod>('term');
  const [at, setAt] = useState<string | undefined>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [frameworkId, setFrameworkId] = useState<string | undefined>();
  const [subject, setSubject] = useState<string | undefined>();
  const [age, setAge] = useState<string | undefined>();
  const [downloading, setDownloading] = useState<'pdf' | 'csv' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const customProblem = period === 'custom' ? rangeProblem(from, to) : null;
  // Until the custom dates make sense, keep showing the last period that did.
  const [shown, setShown] = useState<HubQuery>({ period: 'term' });
  const q: HubQuery = customProblem ? shown : { period, at, from, to, frameworkId, subject, age };
  if (!customProblem && hubParams(q) !== hubParams(shown)) setShown(q);
  const hub = useHub(q);
  const wellbeing = useWellbeing();

  const choosePeriod = (p: HubPeriod) => {
    if (p === 'custom' && !from && !to && hub.data) {
      // Start from the period on screen (its `to` is the day after it ends), up to today.
      const last = ymd(new Date(Date.parse(`${hub.data.period.to}T12:00:00`) - 86_400_000));
      const today = ymd(new Date());
      setFrom(hub.data.period.from);
      setTo(today < last ? today : last);
    }
    setPeriod(p);
  };
  const download = async (kind: 'pdf' | 'csv') => {
    setDownloading(kind);
    setDownloadError(null);
    try {
      const name = hub.data ? `${hub.data.school.name} ${hub.data.period.label}` : '';
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
      await downloadFile(`/schools/mine/hub.${kind}?${hubParams(q)}`, `educaitor-hub${slug ? `-${slug}` : ''}.${kind}`);
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : 'That download didn’t work. Try again.');
    } finally {
      setDownloading(null);
    }
  };

  if (hub.isLoading) return <Spinner />;
  if (hub.isError || !hub.data) return <Notice tone="error">Couldn&rsquo;t load the Leadership Hub. Try again in a moment.</Notice>;
  const h = hub.data;

  return (
    <div className="page hub">
      <header className="hub-head">
        <div className="page-head">
          <p className="eyebrow">{h.school.name}</p>
          <h1>Leadership Hub</h1>
          <p className="muted">How your team is developing, and where support will help most. Development, not judgement.</p>
        </div>
        <div className="hub-downloads hub-noprint">
          <Button variant="secondary" onClick={() => download('pdf')} loading={downloading === 'pdf'} disabled={!!downloading}>
            Download report
          </Button>
          <Button variant="ghost" onClick={() => download('csv')} loading={downloading === 'csv'} disabled={!!downloading}>
            Spreadsheet
          </Button>
        </div>
      </header>
      {downloadError ? <Notice tone="error">{downloadError}</Notice> : null}

      <div className="hub-controls hub-noprint">
        <div className="segmented" role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p.key} type="button" aria-pressed={period === p.key} onClick={() => choosePeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        {period === 'term' && (h.period.terms?.length ?? 0) > 1 ? (
          <label className="hub-select">
            <span>Term</span>
            <select className="select" value={at ?? ''} onChange={(e) => setAt(e.target.value || undefined)}>
              {h.period.terms!.map((t, i) => (
                <option key={t.from} value={i === 0 ? '' : t.from}>
                  {t.label}
                  {i === 0 ? ' (now)' : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {period === 'custom' ? (
          <div className="hub-dates">
            <label className="hub-select">
              <span>From</span>
              <input className="select" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="hub-select">
              <span>to</span>
              <input className="select" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            {customProblem ? <span className="hub-dates-hint">{customProblem}</span> : null}
          </div>
        ) : null}
        {h.frameworks.length > 1 ? (
          <label className="hub-select">
            <span>Framework</span>
            <select className="select" value={h.framework?.id ?? ''} onChange={(e) => setFrameworkId(e.target.value || undefined)}>
              {h.frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {h.filters.subjects.length > 1 ? (
          <label className="hub-select">
            <span>Subject</span>
            <select className="select" value={subject ?? ''} onChange={(e) => setSubject(e.target.value || undefined)}>
              <option value="">All subjects</option>
              {h.filters.subjects.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
        ) : null}
        {h.filters.ages.length > 1 ? (
          <label className="hub-select">
            <span>Ages</span>
            <select className="select" value={age ?? ''} onChange={(e) => setAge(e.target.value || undefined)}>
              <option value="">All ages</option>
              {h.filters.ages.map((a) => (
                <option key={a} value={a}>
                  {a.replace(/^Ages /, '')}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <p className="hub-period">
        {h.period.label}
        {h.framework ? <> &middot; {h.framework.name}</> : null}
        {hub.isFetching ? <span className="faint"> &middot; updating…</span> : null}
      </p>

      {h.filterWithheld ? (
        <Notice>
          Scores are hidden for this filter: fewer than {h.minGroup} teachers would be on one side of it, so comparing views could reveal someone&rsquo;s
          scores. Activity figures still show.
        </Notice>
      ) : null}

      <AtAGlance h={h} />
      <Priorities h={h} />
      <Adoption h={h} />
      <Areas h={h} />
      <StrengthsAndSteps h={h} />
      <DevelopmentTargets />
      <Checkpoints h={h} />
      <Loop h={h} />
      <WellbeingPanel data={wellbeing.data} minGroup={h.minGroup} />
      <Individuals h={h} />

      <p className="faint hub-footnote">
        Scores come only from lessons teachers have shared with a leader here, and leaders&rsquo; own lessons. A figure only shows when at least {h.minGroup}{' '}
        teachers are behind it, and there are no rankings. Each score stays pinned to the version of the framework it was marked against. Individual reports are
        in <Link to="/shared">Shared with you</Link>.
      </p>
    </div>
  );
}

function AtAGlance({ h }: { h: Hub }) {
  const a = h.adoption;
  return (
    <section className="panel">
      <div className="stat-row hub-glance">
        <div className="stat">
          <span className="stat-value">
            {a.activeThisWeek}
            <span className="hub-of"> / {a.staff}</span>
          </span>
          <span className="stat-label">staff recorded this week</span>
        </div>
        <div className="stat">
          <span className="stat-value">{a.lessons}</span>
          <span className="stat-label">
            lessons {lessonsWhen(h)}
            {a.lessonsBefore ? <span className="faint"> ({a.lessonsBefore} before)</span> : null}
          </span>
        </div>
        <div className="stat">
          <span className="stat-value">{h.loop.withFocus}</span>
          <span className="stat-label">staff working on a focus</span>
        </div>
        <div className="stat">
          <span className="stat-value">
            {h.loop.focusMeasured ? (
              <>
                {h.loop.focusImproving}
                <span className="hub-of"> / {h.loop.focusMeasured}</span>
              </>
            ) : (
              '–'
            )}
          </span>
          <span className="stat-label">focuses improving so far</span>
        </div>
      </div>
    </section>
  );
}

function Priorities({ h }: { h: Hub }) {
  if (!h.priorities.length) return null;
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Your priorities</h2>
        <p className="muted">
          The areas you chose in <Link to="/school">organisation settings</Link>, tracked through the framework indicators that match them
          {h.school.scoreMode === 'trends' ? ', and whether they are improving since ' + since(h.period.previousLabel) : ' (out of 5)'}.
        </p>
      </div>
      <div className="hub-areas">
        {h.priorities.map((p) => (
          <div key={p.text} className="panel hub-area hub-priority">
            <div className="hub-area-top">
              <h3>{p.text}</h3>
              {h.school.scoreMode === 'trends' ? (
                <DirectionTag d={p.direction} />
              ) : p.average != null ? (
                <div className="hub-area-score">
                  <span className="hub-score">{one(p.average)}</span>
                  <Change now={p.average} before={p.previous} />
                </div>
              ) : null}
            </div>
            {!p.indicators.length ? (
              <p className="muted">
                {h.framework ? `Nothing in ${h.framework.name} matches this yet. Try wording it like one of the framework’s areas.` : 'Shown once teachers record lessons.'}
              </p>
            ) : (
              <>
                {heldBack(p, h) ? (
                  <p className="muted">{heldBackText(p, h)}</p>
                ) : p.previous != null ? (
                  <p className="faint hub-area-meta">
                    {one(p.previous)} in {since(h.period.previousLabel)}
                  </p>
                ) : null}
                <p className="faint hub-area-meta">Tracked through {p.indicators.map((i) => i.name.toLowerCase()).join('; ')}.</p>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function WeeklyBars({ weeks }: { weeks: Hub['adoption']['weekly'] }) {
  // Fill in quiet weeks so the chart shows gaps honestly.
  const byWeek = new Map(weeks.map((w) => [w.week, w]));
  const now = new Date();
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - ((now.getUTCDay() + 6) % 7)));
  const series = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(monday.getTime() - (11 - i) * 7 * 86_400_000);
    const key = d.toISOString().slice(0, 10);
    return {
      key,
      label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      lessons: byWeek.get(key)?.lessons ?? 0,
      teachers: byWeek.get(key)?.teachers ?? 0,
    };
  });
  // Headroom above the tallest bar for its number.
  const max = Math.max(4, ...series.map((s) => s.lessons)) * 1.15;
  const total = series.reduce((n, s) => n + s.lessons, 0);
  const busiest = series.reduce((b, s) => (s.lessons > b.lessons ? s : b), series[0]);
  return (
    <div className="hub-weekly">
      <p className="hub-weekly-sum">
        <strong>{total}</strong> {total === 1 ? 'lesson' : 'lessons'} in the last 12 weeks
        {busiest.lessons ? <span className="faint"> &middot; busiest the week of {busiest.label}</span> : null}
      </p>
      <div className="hub-weekly-chart" role="img" aria-label={`Lessons recorded each week, last 12 weeks: ${total} in total`}>
        {series.map((s, i) => {
          const current = i === series.length - 1;
          return (
            <div
              key={s.key}
              className={`hub-weekly-col${current ? ' is-current' : ''}`}
              title={`Week of ${s.label}: ${s.lessons} ${s.lessons === 1 ? 'lesson' : 'lessons'} by ${s.teachers} staff${current ? ' (so far)' : ''}`}
            >
              <div className="hub-weekly-track">
                {s.lessons ? <span className="hub-weekly-value">{s.lessons}</span> : null}
                <div className="hub-weekly-bar" style={{ height: `${(s.lessons / max) * 100}%` }} />
              </div>
              <span className="hub-weekly-label">{current ? 'This week' : i % 3 === 2 ? s.label : ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Adoption({ h }: { h: Hub }) {
  const nudge = useNudge();
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const quiet = h.adoption.quiet;
  const send = async (ids: string[]) => {
    setMessage(null);
    try {
      const r = await nudge.mutateAsync({ who: ids });
      setMessage({
        tone: 'success',
        text: `Nudge sent to ${r.sent === 1 ? '1 person' : `${r.sent} people`}.${r.skipped.length ? ` ${r.skipped.length} nudged in the last week, so left alone.` : ''}`,
      });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    }
  };
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Who&rsquo;s recording</h2>
        <p className="muted">Lessons recorded each week across the team. Activity only, never performance.</p>
      </div>
      <div className="hub-grid-2">
        <div className="panel hub-weekly-panel">
          <WeeklyBars weeks={h.adoption.weekly} />
        </div>
        <div className="panel">
          <div className="hub-quiet-head">
            <h3>Not recorded for two weeks</h3>
            {quiet.length > 1 ? (
              <Button variant="secondary" className="hub-noprint" onClick={() => send(quiet.map((q) => q.id))} loading={nudge.isPending}>
                Nudge all
              </Button>
            ) : null}
          </div>
          {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
          {quiet.length === 0 ? (
            <p className="muted">Everyone has recorded in the last two weeks.</p>
          ) : (
            <ul className="hub-quiet">
              {quiet.map((q) => (
                <li key={q.id}>
                  <span>
                    <strong>{q.name}</strong>
                    <span className="faint"> &middot; {q.lastLessonAt ? `last lesson ${relativeDate(q.lastLessonAt).toLowerCase()}` : 'no lessons yet'}</span>
                  </span>
                  <Button variant="ghost" className="hub-noprint" onClick={() => send([q.id])} disabled={nudge.isPending}>
                    Nudge
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

const LEVEL_NAMES = ['1', '2', '3', '4', '5'];

function Distribution({ counts }: { counts: number[] }) {
  const total = counts.reduce((s, x) => s + x, 0);
  if (!total) return null;
  return (
    <div className="hub-dist" role="img" aria-label={`Scores given: ${counts.map((c, i) => `${c} at ${i + 1}`).join(', ')}`}>
      {counts.map((c, i) =>
        c ? <span key={i} className={`hub-dist-part lvl-${i + 1}`} style={{ flexGrow: c }} title={`${c} scored ${LEVEL_NAMES[i]}`} /> : null,
      )}
    </div>
  );
}

function AreaCard({ a, h, previousLabel, trends }: { a: HubArea; h: Hub; previousLabel: string; trends: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="panel hub-area">
      <div className="hub-area-top">
        <h3>{a.name}</h3>
        {trends ? (
          <DirectionTag d={a.direction} />
        ) : a.average != null ? (
          <div className="hub-area-score">
            <span className="hub-score">{one(a.average)}</span>
            <Change now={a.average} before={a.previous} />
          </div>
        ) : null}
      </div>
      {heldBack(a, h) ? (
        <p className="muted">{heldBackText(a, h)}</p>
      ) : (
        <>
          {a.distribution ? <Distribution counts={a.distribution} /> : null}
          <p className="faint hub-area-meta">
            {a.teachers} teachers &middot; {a.lessons} lessons
            {a.previous != null ? (
              <>
                {' '}
                &middot; {one(a.previous)} in {since(previousLabel)}
              </>
            ) : null}
            {a.notSeenRate ? <> &middot; not seen {pct(a.notSeenRate)} of the time</> : null}
          </p>
          <button type="button" className="hub-link hub-noprint" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide indicators' : 'See indicators'}
          </button>
          {open ? (
            <ul className="hub-indicators">
              {a.indicators.map((i) => (
                <li key={i.id}>
                  <span>{i.name}</span>
                  <span className="hub-ind-score">{trends ? <DirectionTag d={i.direction} /> : i.average != null ? one(i.average) : '–'}</span>
                  <span className="faint hub-ind-seen">{i.notSeenRate ? `not seen ${pct(i.notSeenRate)}` : ''}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}

function Areas({ h }: { h: Hub }) {
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Development areas</h2>
        {h.school.scoreMode === 'trends' ? (
          <p className="muted">
            Whether each area of the framework is improving, stable or dipping across the team since {since(h.period.previousLabel)}. From every lesson
            your staff record, anonymously: at least {h.minGroup} teachers behind each, and nobody named.
          </p>
        ) : (
          <p className="muted">
            The team&rsquo;s average in each area of the framework (out of 5), and the change since {since(h.period.previousLabel)}. The bar shows how the
            individual scores are spread, from 1 (lightest) to 5 (darkest). From every lesson your staff record, anonymously: at least {h.minGroup} teachers behind
            each figure, and nobody named.
          </p>
        )}
      </div>
      {h.framework?.earlierVersions && h.framework.versionFrom ? (
        <Notice>
          {h.framework.name} changed on {new Date(h.framework.versionFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}. These figures use
          lessons marked against the current version only, so they compare like with like.
        </Notice>
      ) : null}
      {!h.framework ? (
        <div className="panel panel-tint">
          <p>No lessons yet. Once teachers record lessons in the app, the team&rsquo;s picture appears here.</p>
        </div>
      ) : (
        <div className="hub-areas">
          {h.areas.map((a) => (
            <AreaCard key={a.id} a={a} h={h} previousLabel={h.period.previousLabel} trends={h.school.scoreMode === 'trends'} />
          ))}
        </div>
      )}
    </section>
  );
}

function StrengthsAndSteps({ h }: { h: Hub }) {
  if (!h.strengths.length && !h.nextSteps.length) return null;
  return (
    <section className="hub-section">
      <div className="hub-grid-2">
        <div className="panel">
          <h2 className="hub-h2-small">Going well across the team</h2>
          <ol className="hub-rank-list">
            {h.strengths.map((s) => (
              <li key={s.id}>
                <span>{s.name}</span>
                {s.average != null ? <span className="hub-ind-score">{one(s.average)}</span> : null}
              </li>
            ))}
          </ol>
        </div>
        <div className="panel">
          <h2 className="hub-h2-small">Most common next steps</h2>
          <ol className="hub-rank-list">
            {h.nextSteps.map((s) => (
              <li key={s.id}>
                <span>
                  {s.name}
                  {s.example ? <span className="hub-example">e.g. &ldquo;{s.example}&rdquo;</span> : null}
                </span>
                {s.average != null ? <span className="hub-ind-score">{one(s.average)}</span> : null}
              </li>
            ))}
          </ol>
          <p className="faint">A good place to aim training or coaching.</p>
        </div>
      </div>
    </section>
  );
}

/** The term's development targets at a glance, from the Development page. */
function DevelopmentTargets() {
  const targets = useTargets();
  const active = targets.data?.targets.filter((t) => t.status === 'active') ?? [];
  return (
    <section className="hub-section hub-noprint">
      <div className="hub-section-head">
        <h2>Development targets</h2>
        <p className="muted">
          {active.length
            ? 'What the team is working on, and how many have reflected at the latest check-in.'
            : 'Turn a next step or priority into a target for the term: teachers work on it and reflect in the app.'}
        </p>
      </div>
      {active.length ? (
        <div className="panel">
          <ul className="hub-indicators">
            {active.map((t) => {
              const open = [...t.checkpoints].reverse().find((c) => c.state === 'open');
              return (
                <li key={t.id}>
                  <span>
                    <strong>{t.title}</strong>
                    <span className="faint"> &middot; {t.audienceLabel}</span>
                  </span>
                  <span className="hub-ind-score">{open ? `${open.reflected}/${t.audienceSize}` : '–'}</span>
                  <span className="faint hub-ind-seen">{open ? `reflected · ${open.label}` : `first check-in ${dayMonth(t.checkpoints[0]?.dueOn ?? t.startsOn)}`}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <p>
        <Link to="/development">{active.length ? 'Open Development' : 'Set a development target'}</Link>
      </p>
    </section>
  );
}

function Checkpoints({ h }: { h: Hub }) {
  if (!h.framework || !h.checkpoints.length) return null;
  const any = h.checkpoints.some((c) => c.areas.some((a) => a.teachers >= h.minGroup));
  if (!any) return null;
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Term by term</h2>
        <p className="muted">
          {h.school.scoreMode === 'trends'
            ? 'Each area term by term: improving, stable or dipping against the term before.'
            : 'Each area term by term, from every lesson your staff recorded that term, anonymously.'}
        </p>
      </div>
      <div className="panel hub-table-wrap">
        <table className="hub-table">
          <thead>
            <tr>
              <th>Area</th>
              {h.checkpoints.map((c) => (
                <th key={c.label}>
                  {c.label}
                  {c.version !== h.framework?.version ? <sup className="hub-sup">*</sup> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {h.areas.map((a) => (
              <tr key={a.id}>
                <td>{a.name}</td>
                {h.checkpoints.map((c) => {
                  const cell = c.areas.find((x) => x.id === a.id);
                  const v = cell?.average ?? null;
                  if (h.school.scoreMode === 'trends') {
                    const enough = (cell?.teachers ?? 0) >= h.minGroup;
                    return (
                      <td key={c.label}>
                        {!enough ? <span className="faint">–</span> : cell?.direction ? <DirectionTag d={cell.direction} /> : <span className="faint">Starting point</span>}
                      </td>
                    );
                  }
                  return (
                    <td key={c.label} className="hub-num">
                      {v == null ? <span className="faint">–</span> : one(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint">
          &ldquo;–&rdquo; means fewer than {h.minGroup} teachers recorded lessons that term, not counting you.
          {h.checkpoints.some((c) => c.version !== h.framework?.version)
            ? ' * Marked against an earlier version of the framework, before it was changed, so compare with care.'
            : ''}
        </p>
      </div>
    </section>
  );
}

function Loop({ h }: { h: Hub }) {
  const l = h.loop;
  const answered = l.tried.yes + l.tried.partly + l.tried.notYet;
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Closing the loop</h2>
        <p className="muted">Lesson, feedback, choosing one thing to work on, trying it, and seeing it move. Counts only, never names.</p>
      </div>
      <div className="panel">
        <div className="stat-row">
          <div className="stat">
            <span className="stat-value">{l.lessons ? pct(l.lessonsWithFocus / l.lessons) : '–'}</span>
            <span className="stat-label">of lessons had a focus to try</span>
          </div>
          <div className="stat">
            <span className="stat-value">{answered ? pct((l.tried.yes + l.tried.partly) / answered) : '–'}</span>
            <span className="stat-label">of the time teachers tried it (yes or partly)</span>
          </div>
          <div className="stat">
            <span className="stat-value">{l.focusMeasured ? pct(l.focusImproving / l.focusMeasured) : '–'}</span>
            <span className="stat-label">of focuses improving on their own measure</span>
          </div>
          <div className="stat">
            <span className="stat-value">{l.reflections}</span>
            <span className="stat-label">reflections written after lessons</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Spark({ values, className }: { values: (number | null)[]; className: string }) {
  const pts = values.map((v, i) => ({ v, i })).filter((p) => p.v != null) as { v: number; i: number }[];
  if (pts.length < 2) return null;
  const W = 300;
  const H = 60;
  const x = (i: number) => (i / Math.max(1, values.length - 1)) * W;
  const y = (v: number) => H - ((v - 1) / 4) * H;
  return <polyline className={className} fill="none" points={pts.map((p) => `${x(p.i)},${y(p.v)}`).join(' ')} />;
}

function WellbeingPanel({ data, minGroup }: { data: ReturnType<typeof useWellbeing>['data']; minGroup: number }) {
  if (!data) return null;
  const weekly = data.trends.weekly;
  const mood = data.thisWeek.avgMood;
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>Staff wellbeing</h2>
        <p className="muted">From anonymous check-ins in the app. Nothing shows for a week with fewer than {minGroup} people checking in.</p>
      </div>
      <div className="panel hub-wellbeing">
        <div className="stat-row">
          <div className="stat">
            <span className="stat-value">{mood != null ? `${mood.toFixed(1)} / 5` : '–'}</span>
            <span className="stat-label">average mood this week</span>
          </div>
          <div className="stat">
            <span className="stat-value">{data.thisWeek.avgEnergy != null ? `${data.thisWeek.avgEnergy.toFixed(1)} / 10` : '–'}</span>
            <span className="stat-label">average energy this week</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {data.thisWeek.teachersWithCheckins}
              <span className="hub-of"> / {data.thisWeek.teacherCount}</span>
            </span>
            <span className="stat-label">staff checked in this week</span>
          </div>
        </div>
        {weekly.filter((w) => w.avgMood != null).length > 1 ? (
          <svg className="hub-spark" viewBox="0 0 300 60" preserveAspectRatio="none" role="img" aria-label="Average mood by week">
            <Spark values={weekly.map((w) => w.avgMood)} className="hub-spark-mood" />
          </svg>
        ) : null}
        {weekly.filter((w) => w.avgMood != null).length > 1 ? (
          <p className="faint hub-area-meta">
            Average mood week by week, from the week of {day(weekly[0].label)} to {day(weekly[weekly.length - 1].label)}. Higher is better; gaps are weeks with too
            few check-ins to show.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function Individuals({ h }: { h: Hub }) {
  const mode = SCORE_MODE_OPTIONS.find((o) => o.mode === h.school.scoreMode) ?? SCORE_MODE_OPTIONS[1];
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>{h.school.scoreMode === 'full' ? 'Individual teachers' : 'How scores are shown'}</h2>
        <p className="muted">
          {h.school.scoreMode === 'full'
            ? 'Each teacher’s scores from the lessons they have shared with leaders here. In name order; not a ranking.'
            : `${mode.title}: ${mode.leaders.charAt(0).toLowerCase()}${mode.leaders.slice(1)}`}
        </p>
      </div>
      {h.school.scoreMode === 'full' && h.people ? (
        <div className="panel hub-table-wrap">
          {h.people.length === 0 ? (
            <p className="muted">No shared lessons this period.</p>
          ) : (
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Shared lessons</th>
                  <th>Average</th>
                  <th>Latest</th>
                  <th>Direction</th>
                  <th>Working on</th>
                </tr>
              </thead>
              <tbody>
                {h.people.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/shared?staff=${p.id}`}>{p.name}</Link>
                    </td>
                    <td className="hub-num">{p.lessons}</td>
                    <td className="hub-num">{one(p.average)}</td>
                    <td className="hub-num">{one(p.latest)}</td>
                    <td>{p.trend ? <DirectionTag d={p.trend} /> : <span className="faint">–</span>}</td>
                    <td className="hub-focus-cell">{p.focus ?? <span className="faint">–</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
      <p className="faint hub-noprint">
        {h.school.canChangeScoreMode ? (
          <>
            Change how scores are shown in <Link to="/school">organisation settings</Link>.
          </>
        ) : (
          'An owner of your organisation can change how scores are shown.'
        )}
      </p>
    </section>
  );
}
